/**
 * The parse-vs-extracted comparison — PD-5, spec §10.3 / §11's "parity view".
 *
 * Two things on one screen, because they are two zoom levels of one decision:
 *
 *  1. **The per-family agreement table.** Per field family: agree, disagree,
 *     the two one-sided counts, and the ratio against the 0.90 gate. It is the
 *     evidence an operator needs before adding a family to `EXTRACTED_FAMILIES`
 *     — which is the entire cutover. These are the numbers the offline M10
 *     check produces; `parity-model.ts` is pinned to the same golden fixture so
 *     "the table matches M10" is a test, not a hope.
 *  2. **The per-field side-by-side.** What `parse(story_text)` found next to
 *     what the reasoner claimed, with the status the reasoner claimed it under.
 *     A ratio tells you a family disagrees; only this tells you it disagreed
 *     about `history.howLongKnown` because one said "two years" and the other
 *     "2 years".
 *
 * Nothing here re-derives a verdict. `verdict` is computed by the writer, at
 * ingest, from values this screen no longer has (member sets, normalization,
 * the transliteration set), and a second opinion rendered next to the first
 * would be a second definition. The screen shows the rendered members and the
 * verdict that was recorded.
 *
 * AMENDED 2026-09-12 (M-PARSE-CTX). The matcher's parse now receives the
 * reasoner's `extracted` as prompt hints, which makes a THIRD thing true on
 * this screen: the agreement table above no longer describes the story that
 * shipped. It describes the `shadow` rows — a hint-free parse of the same text,
 * sampled 1 in N — because a parse that has read the answer agrees with it more
 * often, and a ratio computed over those would be a gauge welded to green. The
 * hint strip says so out loud, and it is rendered whenever the worker sends one
 * rather than defaulted to zeros: "no hints measured" and "this worker cannot
 * measure hints" are opposite statements, the same way `not measured` and
 * `below gate` are.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ExtractedParityReport, ParityCounts, ParityRowRecord } from '../../api/types';
import {
  PARITY_MIN_COMPARISONS,
  familyRows,
  formatRatio,
  groupRowsByFamily,
  splitMembers,
  type CutoverState,
} from './parity-model';

interface Props {
  /** `null` while loading, or when the read failed — `unavailable` says which. */
  report: ExtractedParityReport | null;
  /** The 503 reason (`extracted_parity_unavailable`: migration 031 not applied)
   *  or any other read error. An empty table would read as "no divergence". */
  unavailable?: string | null;
  title?: string;
  /** What window / which pebbles these numbers cover. */
  scope?: string;
  loading?: boolean;
}

const STATE_LABEL: Record<CutoverState, string> = {
  live: 'live',
  ready: 'ready to flip',
  thin: 'needs evidence',
  below: 'below gate',
  unmeasured: 'not measured',
};

const STATE_CLASS: Record<CutoverState, string> = {
  live: 'bg-emerald-100 text-emerald-800',
  ready: 'bg-sky-100 text-sky-800',
  // Deliberately NOT the `ready` blue: a family at 100% on eleven comparisons
  // looked exactly like a flippable one before F-PB10-5, and the colour is
  // what an operator actually reads off this table.
  thin: 'bg-indigo-50 text-indigo-700',
  below: 'bg-amber-100 text-amber-800',
  unmeasured: 'bg-gray-100 text-gray-500',
};

const VERDICT_CLASS: Record<string, string> = {
  agree: 'bg-emerald-100 text-emerald-700',
  disagree: 'bg-rose-100 text-rose-700',
  extracted_only: 'bg-sky-100 text-sky-700',
  parse_only: 'bg-amber-100 text-amber-800',
};

/** M-PARSE-CTX. `hint_filled` is the only one that changed an outcome, so it is
 *  the only one that gets a colour an eye stops on. */
const PROVENANCE_CLASS: Record<string, string> = {
  parse_only: 'bg-gray-100 text-gray-600',
  hint_confirmed: 'bg-slate-100 text-slate-600',
  hint_filled: 'bg-violet-100 text-violet-800',
};

const STATUS_CLASS: Record<string, string> = {
  stated: 'bg-gray-100 text-gray-700',
  uncertain: 'bg-amber-50 text-amber-700',
  declined: 'bg-purple-100 text-purple-700',
  unknown: 'bg-gray-50 text-gray-500',
};

function StateBadge({ state }: { state: CutoverState }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${STATE_CLASS[state]}`}>{STATE_LABEL[state]}</span>
  );
}

function CountCells({ counts }: { counts: ParityCounts }) {
  return (
    <>
      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{counts.agree}</td>
      <td className="px-3 py-2 text-right tabular-nums text-rose-700">{counts.disagree}</td>
      <td className="px-3 py-2 text-right tabular-nums text-sky-700">{counts.extracted_only}</td>
      <td className="px-3 py-2 text-right tabular-nums text-amber-700">{counts.parse_only}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{counts.compared}</td>
      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
        {formatRatio(counts.ratio)}
      </td>
    </>
  );
}

/** One member list. Empty is drawn as "(empty)" rather than a blank cell: on
 *  this screen "the side had nothing" IS the finding half the time. */
function Members({ value, tone }: { value: string | null; tone: 'parse' | 'extracted' }) {
  const members = splitMembers(value);
  if (members.length === 0) return <span className="text-xs text-gray-400 italic">(empty)</span>;
  const cls = tone === 'parse' ? 'bg-amber-50 text-amber-900' : 'bg-sky-50 text-sky-900';
  return (
    <div className="flex flex-wrap gap-1">
      {members.map((m, i) => (
        <span key={i} className={`text-xs px-1.5 py-0.5 rounded font-mono ${cls}`}>
          {m}
        </span>
      ))}
    </div>
  );
}

function FieldRow({ row, showProvenance }: { row: ParityRowRecord; showProvenance: boolean }) {
  return (
    <tr
      className="align-top"
      data-testid="parity-field-row"
      data-field={row.field}
      data-verdict={row.verdict}
      data-subject={row.parse_subject ?? undefined}
      data-provenance={row.provenance ?? undefined}
    >
      <td className="px-3 py-2 font-mono text-xs text-gray-800 whitespace-nowrap">{row.field}</td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className={`text-xs px-2 py-0.5 rounded ${VERDICT_CLASS[row.verdict] ?? 'bg-gray-100 text-gray-600'}`}>
          {row.verdict}
        </span>
        {row.parse_subject === 'hinted' && (
          <span
            className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700"
            title="Measured against the parse that SHIPPED, which had already read the hints — excluded from the family ratio."
          >
            hinted
          </span>
        )}
      </td>
      {showProvenance && (
        <td className="px-3 py-2 whitespace-nowrap">
          {row.provenance ? (
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                PROVENANCE_CLASS[row.provenance] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {row.provenance}
            </span>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </td>
      )}
      <td className="px-3 py-2 w-2/5">
        <Members value={row.parse_value} tone="parse" />
      </td>
      <td className="px-3 py-2 w-2/5">
        <Members value={row.extracted_value} tone="extracted" />
        {row.extracted_status && (
          <span
            className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded ${
              STATUS_CLASS[row.extracted_status] ?? 'bg-gray-50 text-gray-500'
            }`}
          >
            {row.extracted_status}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">
        {row.pebble_id ? (
          <Link to={`/conversation/${row.pebble_id}`} className="text-pebbl-600 hover:underline">
            timeline →
          </Link>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}

export default function ParityReport({ report, unavailable, title, scope, loading }: Props) {
  const [showRows, setShowRows] = useState(true);

  if (loading) {
    return (
      <div className="card text-sm text-gray-500" data-testid="parity-report">
        Loading parity…
      </div>
    );
  }

  if (unavailable || !report) {
    return (
      <div className="card space-y-1" data-testid="parity-report">
        <h2 className="text-lg font-semibold text-gray-900">{title ?? 'extracted_v1 parity'}</h2>
        <p className="text-sm text-amber-700" data-testid="parity-unavailable">
          {unavailable ?? 'No parity report loaded.'}
        </p>
        <p className="text-xs text-gray-500">
          An empty table would read as “no divergence”, which is not what a missing
          measurement means.
        </p>
      </div>
    );
  }

  const families = familyRows(report);
  const groups = groupRowsByFamily(report.rows ?? []);
  const never = report.summary.never_applied;
  const rules = report.rules;
  const hints = report.hints;
  // The column appears when the worker can fill it. Rendering an all-dash
  // column against a pre-M-PARSE-CTX worker would read as "nothing was
  // attributable", which is a measurement, not a missing feature.
  const showProvenance = (report.rows ?? []).some((r) => r.parse_subject !== undefined);
  const minComparisons =
    report.min_comparisons ?? report.summary.min_comparisons ?? PARITY_MIN_COMPARISONS;

  return (
    <div className="card space-y-4" data-testid="parity-report">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title ?? 'extracted_v1 parity'}</h2>
          <p className="text-xs text-gray-500">
            parse(story_text) vs the reasoner’s <span className="font-mono">extracted</span> — §10.3
            “parity before trust”. A family flips at{' '}
            <span className="font-mono">{formatRatio(report.threshold)}</span> AND at least{' '}
            <span className="font-mono" data-testid="parity-min-comparisons">
              {minComparisons}
            </span>{' '}
            comparisons.
            {scope ? ` ${scope}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!report.parity_enabled && (
            <span
              className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded"
              data-testid="parity-disabled"
            >
              EXTRACTED_PARITY off — nothing new is being measured
            </span>
          )}
          {(report.families_live ?? []).length > 0 && (
            <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded" data-testid="parity-live">
              live: {report.families_live.join(', ')}
            </span>
          )}
        </div>
      </div>

      {/* Per-family agreement — the same numbers the offline M10 check emits. */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Family</th>
              <th className="text-right px-3 py-2 font-medium">Agree</th>
              <th className="text-right px-3 py-2 font-medium">Disagree</th>
              <th className="text-right px-3 py-2 font-medium">Extracted only</th>
              <th className="text-right px-3 py-2 font-medium">Parse only</th>
              <th className="text-right px-3 py-2 font-medium">Compared</th>
              <th className="text-right px-3 py-2 font-medium">Agreement</th>
              <th className="text-left px-3 py-2 font-medium">Cutover</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {families.map(({ family, counts, state }) => (
              <tr key={family} data-testid="parity-family-row" data-family={family} data-state={state}>
                <td className="px-3 py-2 font-mono text-xs text-gray-800">{family}</td>
                <CountCells counts={counts} />
                <td className="px-3 py-2">
                  <StateBadge state={state} />
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-medium" data-testid="parity-overall">
              <td className="px-3 py-2 text-gray-800">overall</td>
              <CountCells counts={report.summary.overall} />
              <td className="px-3 py-2 text-xs text-gray-500">
                {report.summary.overall.at_cutover ? 'at gate' : ''}
              </td>
            </tr>
            {never && (
              <tr className="bg-gray-50/60 text-gray-500" data-testid="parity-never-applied">
                <td className="px-3 py-2 text-xs">
                  never applied
                  <span className="ml-1 font-mono text-[10px]">({never.fields.join(', ') || 'none'})</span>
                </td>
                <CountCells counts={never.counts} />
                <td className="px-3 py-2 text-xs">excluded from the ratio</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">
        A <span className="font-medium">live</span> family reads from the extracted path already and
        has stopped accumulating rows — its counts are frozen at the evidence that justified the
        flip. <span className="font-medium">needs evidence</span> is at or above the ratio on fewer
        than {minComparisons} comparisons — nothing is wrong with it, there is not enough of it.{' '}
        <span className="font-medium">not measured</span> is not{' '}
        <span className="font-medium">below gate</span>: no evidence and evidence against are
        opposite conclusions.
      </p>

      {/* WHICH PARSE THOSE NUMBERS DESCRIBE (M-PARSE-CTX). The table above is
          the hint-free sample; this is everything the sample leaves out. */}
      {hints && (
        <div
          className="border border-violet-200 bg-violet-50/40 rounded-lg px-3 py-2 space-y-1"
          data-testid="parity-hints"
        >
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-800">
              Hints: the parse reads the reasoner’s <span className="font-mono">extracted</span>
            </span>
            <span className="text-xs text-gray-600" data-testid="parity-subjects">
              <span className="font-mono">{hints.subjects.shadow}</span> shadow rows (measured) ·{' '}
              <span className="font-mono">{hints.subjects.hinted}</span> hinted rows (not measured)
              · sampled 1 in <span className="font-mono">{hints.shadow_sample_n}</span>
            </span>
          </div>
          <p className="text-xs text-gray-600">
            The ratios above count only <span className="font-medium">shadow</span> rows — a
            hint-free parse of the same text. A parse that has already read the reasoner’s answer
            agrees with it more often, so hinted rows are recorded and drilled into, never added to
            the gate.
          </p>
          <div className="flex flex-wrap gap-2 pt-0.5" data-testid="parity-provenance">
            {(['hint_filled', 'hint_confirmed', 'parse_only', 'unattributed'] as const).map((k) => (
              <span
                key={k}
                data-provenance-bucket={k}
                className={`text-xs px-2 py-0.5 rounded ${
                  PROVENANCE_CLASS[k] ?? 'bg-gray-100 text-gray-500'
                }`}
              >
                {k} <span className="font-mono">{hints.provenance[k]}</span>
              </span>
            ))}
            {hints.hinted.compared > 0 && (
              <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800" data-testid="parity-hinted-ratio">
                shipped-parse agreement{' '}
                <span className="font-mono">{formatRatio(hints.hinted.ratio)}</span> on{' '}
                <span className="font-mono">{hints.hinted.compared}</span> — not a gate
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            <span className="font-medium">hint_filled</span> is the value the hint supplied where
            the hint-free parse had nothing — the only bucket that is an argument for hints.{' '}
            <span className="font-medium">parse_only</span> includes the parse reading a hint and
            keeping its own answer, which is what the prompt asks of it.
          </p>
        </div>
      )}

      {/* THE RULES THAT PRODUCED THE NUMBERS. Straight from the response, not
          from a copy in this repo: they were amended once (F-PB10-5) and a
          table read without them says something it does not mean. */}
      {rules && (
        <details className="text-xs text-gray-500" data-testid="parity-rules">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
            Comparison rules — <span className="font-mono">{rules.amended}</span>
            {rules.version && (
              <>
                {' '}
                <span className="font-mono text-[10px]" data-testid="parity-rules-version">
                  (rules_version {rules.version})
                </span>
              </>
            )}
          </summary>
          <dl className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-200">
            <div>
              <dt className="font-medium text-gray-700">Cutover</dt>
              <dd>{rules.cutover}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700">Members</dt>
              <dd>{rules.member_comparison}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700">
                Containment{' '}
                <span className="font-mono text-[10px]" data-testid="parity-containment-keys">
                  ({rules.containment_keys.join(', ') || 'none'})
                </span>
              </dt>
              <dd>{rules.containment}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700">
                Never applied{' '}
                <span className="font-mono text-[10px]">
                  ({rules.never_applied_keys.join(', ') || 'none'})
                </span>
              </dt>
              <dd>{rules.never_applied}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700">Strict families</dt>
              <dd>{rules.strict_families}</dd>
            </div>
            {rules.subjects && (
              <div>
                <dt className="font-medium text-gray-700">Parse subject</dt>
                <dd>{rules.subjects}</dd>
              </div>
            )}
            {rules.provenance && (
              <div>
                <dt className="font-medium text-gray-700">Provenance</dt>
                <dd>{rules.provenance}</dd>
              </div>
            )}
          </dl>
        </details>
      )}

      {/* Per-field: the actual side-by-side. */}
      {groups.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowRows((v) => !v)}
            className="text-sm text-pebbl-700 hover:underline"
          >
            {showRows ? '− Hide' : '+ Show'} field-by-field comparison ({report.rows.length} rows)
          </button>
          {showRows &&
            groups.map((group) => (
              <div key={group.family} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-1.5 text-xs uppercase tracking-wide text-gray-500 font-medium">
                  {group.family}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-xs uppercase text-gray-400">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-medium">Field</th>
                        <th className="text-left px-3 py-1.5 font-medium">Verdict</th>
                        {showProvenance && (
                          <th className="text-left px-3 py-1.5 font-medium">Provenance</th>
                        )}
                        <th className="text-left px-3 py-1.5 font-medium">parse(story_text)</th>
                        <th className="text-left px-3 py-1.5 font-medium">extracted (reasoner)</th>
                        <th className="text-left px-3 py-1.5 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {group.rows.map((row) => (
                        <FieldRow
                          key={`${row.request_key}:${row.field}`}
                          row={row}
                          showProvenance={showProvenance}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>
      )}

      {groups.length === 0 && (
        <p className="text-xs text-gray-500" data-testid="parity-no-rows">
          No rows in this window — ask for <span className="font-mono">rows</span> to see the
          field-by-field comparison.
        </p>
      )}
    </div>
  );
}
